import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  API_BASE_URL,
  ApiError,
  RevisionConflictError,
  apiClient,
  authFetch,
} from './client'
import type { components } from './schema'
import { useOptimisticListMutation } from './use-optimistic-mutation'
import {
  parseUploadErrorBody,
  type AiActionRead,
  type AiActionStatus,
  type ChatContextSummary,
  type ChatMessageResponse,
  type ChatSessionDetail,
  type ChatSessionSummary,
  type ContextScope,
  type DocumentComment,
  type DocumentCommentCreate,
  type DocumentCommentUpdate,
  type DocumentContextUpdate,
  type DocumentDetail,
  type DocumentStatus,
  type DocumentSuggestion,
  type DocumentSuggestionCreate,
  type DocumentSummary,
  type DocumentUpdateBody,
  type EditActor,
  type EditProposalRead,
  type EditProposalStatus,
  type EffectiveInstructions,
  type Field,
  type FieldCreateBody,
  type FieldConflictPayload,
  type FieldDefinitionPatchBody,
  type FieldEdit,
  type FillRequestBody,
  type FillResponseBody,
  type JobRead,
  type Overlay,
  type OverlayCreateBody,
  type OverlayStatus,
  type OverlayTransitionBody,
  type OverlayType,
  type OverlayUpdateBody,
  type PatchFieldsBody,
  type PatchSingleFieldBody,
  type RenderRequestBody,
  type RenderResponseBody,
  type RevisionDetail,
  type RevisionSummary,
  type SavedPromptCreate,
  type SavedPromptRead,
  type SavedPromptUpdate,
  type SuggestEditsBody,
  type SuggestEditsResponseBody,
  type SuggestionStatus,
  type TemplateQuickFillBody,
  type TemplateQuickFillResponse,
  type TemplateUpdateBody,
  type WorkspaceContextAuditEvent,
  type WorkspaceContextRead,
  type WorkspaceContextWrite,
} from './docfiller-types'

/**
 * Tiny TanStack Query wrappers around the openapi-fetch client. Treat these
 * as examples — copy the pattern when you add new resources.
 *
 * Once `scripts/codegen.sh` has been run, openapi-fetch will type the path
 * strings, params, and responses for you.
 */

/** Pagination + filter params for the workspace audit query hook. */
export interface WorkspaceAuditParams {
  limit?: number
  offset?: number
}

export const queryKeys = {
  me: ['me'] as const,
  documents: {
    all: ['documents'] as const,
    list: (params?: unknown) => ['documents', 'list', params] as const,
    templates: (params?: unknown) => ['documents', 'templates', params] as const,
    detail: (id: string) => ['documents', 'detail', id] as const,
    audit: (id: string, actor: EditActor | 'all') =>
      ['documents', 'audit', id, actor] as const,
    chats: (id: string) => ['documents', 'chats', id] as const,
    jobs: (id: string) => ['documents', 'jobs', id] as const,
    revisions: (id: string) => ['documents', 'revisions', id] as const,
    revision: (docId: string, revUuid: string) =>
      ['documents', 'revisions', docId, revUuid] as const,
    overlays: (
      id: string,
      filter?: {
        type?: OverlayType | null
        status?: OverlayStatus | null
        page?: number | null
      },
    ) => ['documents', 'overlays', id, filter ?? null] as const,
    shares: (id: string) => ['documents', 'shares', id] as const,
    parties: (id: string) => ['documents', 'parties', id] as const,
    shareLinks: (id: string) => ['documents', 'share-links', id] as const,
    tracking: (id: string) => ['documents', 'tracking', id] as const,
    proposals: (id: string, status?: string | null) =>
      ['documents', 'proposals', id, status ?? 'all'] as const,
    comments: (id: string) => ['documents', 'comments', id] as const,
    suggestions: (id: string) => ['documents', 'suggestions', id] as const,
  },
  chats: {
    all: ['chats'] as const,
    list: ['chats', 'list'] as const,
    detail: (id: string) => ['chats', 'detail', id] as const,
  },
  workspaceContext: ['workspace-context'] as const,
  workspaceContextAudit: ['workspace-context', 'audit'] as const,
  documentWorkspaceContext: (id: string) =>
    ['workspace-context', 'document', id] as const,
  documentWorkspaceContextAudit: (id: string) =>
    ['workspace-context', 'document', id, 'audit'] as const,
  aiActions: {
    all: ['ai-actions'] as const,
    forDocument: (docUuid: string, status?: AiActionStatus | null) =>
      ['ai-actions', 'doc', docUuid, status ?? 'all'] as const,
  },
  savedPrompts: {
    all: ['saved-prompts'] as const,
    list: (workspaceUuid: string, projectUuid?: string | null) =>
      ['saved-prompts', 'list', workspaceUuid, projectUuid ?? null] as const,
  },
  effectiveInstructions: (
    docUuid: string,
    params?: { saved_prompt_uuid?: string | null; user_instructions?: string | null },
  ) => ['documents', 'effective-instructions', docUuid, params ?? null] as const,
  workspaces: {
    all: ['workspaces'] as const,
    list: () => ['workspaces', 'list'] as const,
    detail: (uuid: string) => ['workspaces', 'detail', uuid] as const,
    members: (uuid: string) => ['workspaces', 'members', uuid] as const,
    invites: (uuid: string) => ['workspaces', 'invites', uuid] as const,
    audit: (uuid: string, params?: WorkspaceAuditParams) =>
      ['workspaces', 'audit', uuid, params ?? null] as const,
    projects: {
      all: (workspaceUuid: string) =>
        ['workspaces', 'projects', workspaceUuid] as const,
      // Prefix used to invalidate every cached filter slice at once
      // (e.g. after create/update/delete). ``list(uuid, q)`` extends this
      // prefix with the trimmed search term so a single
      // ``invalidateQueries({ queryKey: listAll(uuid) })`` refetches both
      // the unfiltered list and any active search view.
      listAll: (workspaceUuid: string) =>
        ['workspaces', 'projects', workspaceUuid, 'list'] as const,
      list: (workspaceUuid: string, q?: string | null) =>
        ['workspaces', 'projects', workspaceUuid, 'list', q ?? null] as const,
      detail: (workspaceUuid: string, projectUuid: string) =>
        ['workspaces', 'projects', workspaceUuid, 'detail', projectUuid] as const,
      folders: (workspaceUuid: string, projectUuid: string) =>
        ['workspaces', 'projects', workspaceUuid, projectUuid, 'folders'] as const,
    },
  },
} as const

/**
 * Optimistic-mutation rollback envelope used by the workspace-context
 * mutations. Keeping it shared (rather than re-declared per hook) means
 * tests can introspect the shape once and assert against the rollback
 * behavior the same way for every scope.
 */
interface WorkspaceContextRollback<TQueryKey extends readonly unknown[]> {
  queryKey: TQueryKey
  previous: WorkspaceContextRead | null | undefined
}

/**
 * Pull a {@link FieldConflictPayload} out of a 409 ApiError body, if the
 * server returned one. FastAPI wraps the detail dict in an outer envelope:
 *
 *   {detail: {detail: "Field revision conflict", conflict: FieldConflictPayload}}
 *
 * Bulk-patch responses use ``conflicts: FieldConflictPayload[]`` instead;
 * use {@link extractFieldConflicts} for that.
 */
export function extractFieldConflict(
  err: unknown,
): FieldConflictPayload | null {
  if (!(err instanceof ApiError)) return null
  const body = err.body as { detail?: unknown } | null
  if (!body || typeof body !== 'object') return null
  const inner = (body as { detail?: unknown }).detail
  if (!inner || typeof inner !== 'object') return null
  if ((inner as { detail?: unknown }).detail !== 'Field revision conflict') {
    return null
  }
  const conflict = (inner as { conflict?: unknown }).conflict
  return (conflict as FieldConflictPayload) ?? null
}

export function extractFieldConflicts(err: unknown): FieldConflictPayload[] {
  if (!(err instanceof ApiError)) return []
  const body = err.body as { detail?: unknown } | null
  if (!body || typeof body !== 'object') return []
  const inner = (body as { detail?: unknown }).detail
  if (!inner || typeof inner !== 'object') return []
  if ((inner as { detail?: unknown }).detail !== 'Field revision conflict') {
    return []
  }
  const conflicts = (inner as { conflicts?: unknown }).conflicts
  return Array.isArray(conflicts) ? (conflicts as FieldConflictPayload[]) : []
}

// ---------------------------------------------------------------------------
// /dashboard/me — current user info from the backend
// ---------------------------------------------------------------------------
/**
 * Shape of the ``/dashboard/me`` response. Sourced from the OpenAPI schema
 * (``components.schemas.DashboardMe``) so a backend change to the response
 * model trips a frontend type error instead of silently slipping by.
 *
 * ``is_active`` is the source of truth for the UI read-only state: when
 * the backend marks an account inactive, the SPA mirrors that gate at the
 * UI layer so deactivated users see a read-only banner instead of
 * clicking Save and getting a toast.
 */
export type DashboardMe = components['schemas']['DashboardMe']
export type DashboardMeUpdate = components['schemas']['DashboardMeUpdate']

export function useMe() {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/dashboard/me')
      if (error) throw new ApiError(error)
      return data
    },
  })
}

export function useUpdateMe() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: DashboardMeUpdate): Promise<DashboardMe> => {
      const { data, error } = await apiClient.PATCH('/dashboard/me', { body })
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.me, data)
    },
  })
}

/**
 * True when the current user's account is deactivated and the UI should
 * render in read-only mode (banner + disabled mutation controls). Only
 * flips when the backend has positively confirmed ``is_active: false`` —
 * we don't lock the UI during a transient loading/error state since the
 * route-level auth wall already gates unauthenticated callers and a
 * server-side write attempt would surface a toast anyway.
 */
export function useIsReadOnly(): boolean {
  const { data } = useMe()
  return data?.is_active === false
}

// Statuses that mean the worker is still doing something to the document and
// we should keep polling for an updated detail (e.g. presigned_url appearing
// once the R2 object is in place, or status flipping to ``parsed``).
const IN_PROGRESS_STATUSES = new Set<DocumentStatus>([
  'uploaded',
  'parsing',
  'filling',
])

// ---------------------------------------------------------------------------
// /dashboard/documents — docfiller resource lives under the dashboard
// aggregator (auth-protected via the dashboard router), mirroring the pattern
// used by `useMe()`.
// ---------------------------------------------------------------------------
export interface DocumentListFilter {
  workspaceUuid?: string | undefined
  projectUuid?: string | undefined
  folderUuid?: string | undefined
  noProject?: boolean | undefined
  search?: string | undefined
  state?: 'active' | 'archived' | 'deleted' | 'expired' | undefined
}

export function useDocuments(filter: DocumentListFilter = {}) {
  const { workspaceUuid, projectUuid, folderUuid, noProject, search, state } = filter
  return useQuery({
    queryKey: queryKeys.documents.list(filter),
    queryFn: async (): Promise<DocumentSummary[]> => {
      const query: Record<string, string> = {}
      if (workspaceUuid) query.workspace_uuid = workspaceUuid
      if (noProject) query.no_project = 'true'
      if (projectUuid) query.project_uuid = projectUuid
      if (folderUuid) query.folder_uuid = folderUuid
      if (search?.trim()) query.search = search.trim()
      if (state) query.state = state
      const { data, error } = await apiClient.GET('/dashboard/documents', {
        params: { query },
      })
      if (error) throw new ApiError(error)
      return data ?? []
    },
    // Keep the list fresh while any visible document is still being
    // processed — the per-card progress bar would otherwise freeze at the
    // initial percent until the user navigates away.
    refetchInterval: (query) => {
      const docs = query.state.data as DocumentSummary[] | undefined
      if (!docs?.length) return false
      const stillProcessing = docs.some((d) =>
        IN_PROGRESS_STATUSES.has(d.status),
      )
      return stillProcessing ? 2000 : false
    },
  })
}

export function useDocument(uuid: string | undefined) {
  return useQuery({
    queryKey: queryKeys.documents.detail(uuid ?? ''),
    queryFn: async (): Promise<DocumentDetail> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/documents/{doc_uuid}',
        { params: { path: { doc_uuid: uuid! } } },
      )
      if (error) throw new ApiError(error)
      return data
    },
    enabled: Boolean(uuid),
    refetchInterval: (query) => {
      const doc = query.state.data as DocumentDetail | undefined
      if (!doc) return false
      return IN_PROGRESS_STATUSES.has(doc.status) ? 2000 : false
    },
  })
}

/**
 * Per-upload context settings. ``applyContext`` defaults to true on the
 * server (saved context is snapshotted), so we only send the flag when the
 * user explicitly opted out via the upload-page checkbox.
 *
 * ``initialContextOverride`` (if non-empty) wins over the saved blob — used
 * by callers that want to attach a one-off context bag for a single upload
 * without touching the reusable workspace context.
 */
export interface UploadDocumentInput {
  file: File
  applyContext?: boolean
  initialContextOverride?: string
  isTemplate?: boolean
  templatePrompt?: string
  fillMode?: 'prefill' | 'suggest' | 'do_not_fill'
  workspaceUuid?: string
  projectUuid?: string
  folderUuid?: string
}

export function useUploadDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      input: File | UploadDocumentInput,
    ): Promise<DocumentSummary> => {
      const params = input instanceof File ? { file: input } : input
      const formData = new FormData()
      formData.append('file', params.file)
      if (params.applyContext === false) {
        formData.append('apply_context', 'false')
      }
      if (params.initialContextOverride && params.initialContextOverride.trim()) {
        formData.append(
          'initial_context_override',
          params.initialContextOverride.trim(),
        )
      }
      if (params.isTemplate) {
        formData.append('is_template', 'true')
      }
      if (params.templatePrompt && params.templatePrompt.trim()) {
        formData.append('template_prompt', params.templatePrompt.trim())
      }
      if (params.fillMode) {
        formData.append('fill_mode', params.fillMode)
      }
      if (params.workspaceUuid) {
        formData.append('workspace_uuid', params.workspaceUuid)
      }
      if (params.projectUuid) {
        formData.append('project_uuid', params.projectUuid)
      }
      if (params.folderUuid) {
        formData.append('folder_uuid', params.folderUuid)
      }
      const res = await authFetch(`${API_BASE_URL}/dashboard/documents`, {
        method: 'POST',
        body: formData,
      })
      if (!res.ok) {
        // The server returns ``{ detail: "..." }`` for every reject case
        // (oversize, wrong extension, scanner block, …). Parse it so the
        // toast says e.g. "This file looks unsafe (matched EICAR)."
        // instead of "Upload failed: 422 {detail: ...}".
        const body = await res
          .clone()
          .json()
          .catch(async () => (await res.text().catch(() => '')) || null)
        throw new Error(parseUploadErrorBody(body, res.status))
      }
      return (await res.json()) as DocumentSummary
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents.all })
    },
  })
}

export function useDocumentTemplates(filter: { workspaceUuid?: string } = {}) {
  const { workspaceUuid } = filter
  return useQuery({
    queryKey: queryKeys.documents.templates(filter),
    queryFn: async (): Promise<DocumentSummary[]> => {
      const query: Record<string, string> = {}
      if (workspaceUuid) query.workspace_uuid = workspaceUuid
      const { data, error } = await apiClient.GET('/dashboard/documents/templates', {
        params: { query },
      })
      if (error) throw new ApiError(error)
      return data ?? []
    },
  })
}

export function useUpdateTemplate(documentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: TemplateUpdateBody): Promise<DocumentSummary> => {
      const { data, error } = await apiClient.PATCH(
        '/dashboard/documents/{doc_uuid}/template',
        {
          params: { path: { doc_uuid: documentId } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents.all })
    },
  })
}

export function useQuickFillTemplate(documentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      body: TemplateQuickFillBody,
    ): Promise<TemplateQuickFillResponse> => {
      const { data, error } = await apiClient.POST(
        '/dashboard/documents/{doc_uuid}/quick-fill',
        {
          params: { path: { doc_uuid: documentId } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents.all })
    },
  })
}

/**
 * Apply a {@link PatchSingleFieldBody} to a cached {@link Field} without
 * round-tripping through the server. Used by the optimistic update path of
 * {@link usePatchSingleField} and by the SSE proposal handler so the Fields
 * tab can reflect agent activity before the next poll.
 *
 * Mirrors the server's PATCH semantics in `backend/app/routers/documents.py`:
 * `clear_suggested` wins over an explicit `suggested_value`, and an explicitly
 * sent `current_value: null` clears the value.
 */
export function applyFieldPatch(
  field: Field,
  patch: PatchSingleFieldBody,
  updated_at = new Date().toISOString(),
): Field {
  const next: Field = { ...field, updated_at }
  if ('current_value' in patch) {
    next.current_value = patch.current_value ?? null
  }
  if (patch.clear_suggested) {
    next.suggested_value = null
  } else if ('suggested_value' in patch) {
    next.suggested_value = patch.suggested_value ?? null
  }
  if ('accepted_by' in patch) {
    next.accepted_by = patch.accepted_by ?? null
  }
  return next
}

interface OptimisticDetailContext {
  previous: DocumentDetail | undefined
}

export function usePatchFields(documentId: string) {
  const qc = useQueryClient()
  const detailKey = queryKeys.documents.detail(documentId)
  return useMutation<
    DocumentDetail,
    Error,
    PatchFieldsBody,
    OptimisticDetailContext
  >({
    mutationFn: async (body) => {
      // The OpenAPI codegen marks ``force_overwrite`` as required (because it
      // has a server-side default). We loosen the public type to make the
      // common save path ergonomic; default to ``false`` before hitting the
      // wire so the body matches the generated request type.
      const wireBody = {
        ...body,
        force_overwrite: body.force_overwrite ?? false,
      }
      const { data, error } = await apiClient.PATCH(
        '/dashboard/documents/{doc_uuid}/fields',
        {
          params: { path: { doc_uuid: documentId } },
          body: wireBody,
        },
      )
      if (error) {
        // 409 from the optimistic-concurrency check returns the
        // RevisionConflict body verbatim — unwrap into a typed error so
        // the Save handler can render the overwrite-confirm dialog.
        if (RevisionConflictError.isConflictBody(error)) {
          throw new RevisionConflictError(error)
        }
        throw new ApiError(error)
      }
      return data
    },
    // Optimistically reflect the saved values in the cache so the panel
    // stops showing "unsaved" the moment the user clicks Save. The polling
    // refetch + onSettled invalidation reconcile against the server.
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: detailKey })
      const previous = qc.getQueryData<DocumentDetail>(detailKey)
      if (previous) {
        const patches = new Map(
          body.fields.map((p) => [p.uuid, p.current_value]),
        )
        const now = new Date().toISOString()
        qc.setQueryData<DocumentDetail>(detailKey, {
          ...previous,
          fields: previous.fields.map((f) =>
            patches.has(f.uuid)
              ? {
                  ...f,
                  current_value: patches.get(f.uuid) ?? null,
                  updated_at: now,
                }
              : f,
          ),
        })
      }
      return { previous }
    },
    onError: (_err, _body, context) => {
      if (context?.previous) {
        qc.setQueryData(detailKey, context.previous)
      }
    },
    onSuccess: (data) => {
      qc.setQueryData(detailKey, data)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: detailKey })
      qc.invalidateQueries({
        queryKey: queryKeys.documents.revisions(documentId),
      })
    },
  })
}

export function usePatchSingleField(documentId: string) {
  const qc = useQueryClient()
  const detailKey = queryKeys.documents.detail(documentId)
  return useMutation<
    Field,
    Error,
    { fieldId: string; body: PatchSingleFieldBody },
    OptimisticDetailContext
  >({
    mutationFn: async ({ fieldId, body }) => {
      const { data, error } = await apiClient.PATCH(
        '/dashboard/documents/{doc_uuid}/fields/{field_uuid}',
        {
          params: { path: { doc_uuid: documentId, field_uuid: fieldId } },
          body: { clear_suggested: false, ...body },
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onMutate: async ({ fieldId, body }) => {
      await qc.cancelQueries({ queryKey: detailKey })
      const previous = qc.getQueryData<DocumentDetail>(detailKey)
      if (previous) {
        const now = new Date().toISOString()
        qc.setQueryData<DocumentDetail>(detailKey, {
          ...previous,
          fields: previous.fields.map((f) =>
            f.uuid === fieldId ? applyFieldPatch(f, body, now) : f,
          ),
        })
      }
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        qc.setQueryData(detailKey, context.previous)
      }
    },
    onSuccess: (field, { fieldId }) => {
      qc.setQueryData<DocumentDetail>(detailKey, (previous) =>
        previous
          ? {
              ...previous,
              fields: previous.fields.map((f) =>
                f.uuid === fieldId ? field : f,
              ),
            }
          : previous,
      )
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: detailKey })
      qc.invalidateQueries({
        queryKey: queryKeys.documents.revisions(documentId),
      })
    },
  })
}

export function useCreateDocumentField(documentId: string) {
  const qc = useQueryClient()
  const detailKey = queryKeys.documents.detail(documentId)
  return useMutation<Field, Error, FieldCreateBody>({
    mutationFn: async (body) => {
      const { data, error } = await apiClient.POST(
        '/dashboard/documents/{doc_uuid}/fields',
        {
          params: { path: { doc_uuid: documentId } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: (field) => {
      qc.setQueryData<DocumentDetail>(detailKey, (previous) =>
        previous
          ? {
              ...previous,
              fields: [...previous.fields, field],
            }
          : previous,
      )
      qc.invalidateQueries({ queryKey: detailKey })
    },
  })
}

export function usePatchFieldDefinition(documentId: string) {
  const qc = useQueryClient()
  const detailKey = queryKeys.documents.detail(documentId)
  return useMutation<
    Field,
    Error,
    { fieldId: string; body: FieldDefinitionPatchBody }
  >({
    mutationFn: async ({ fieldId, body }) => {
      const { data, error } = await apiClient.PATCH(
        '/dashboard/documents/{doc_uuid}/fields/{field_uuid}/definition',
        {
          params: { path: { doc_uuid: documentId, field_uuid: fieldId } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: (field, { fieldId }) => {
      qc.setQueryData<DocumentDetail>(detailKey, (previous) =>
        previous
          ? {
              ...previous,
              fields: previous.fields.map((f) =>
                f.uuid === fieldId ? field : f,
              ),
            }
          : previous,
      )
      qc.invalidateQueries({ queryKey: detailKey })
    },
  })
}

export function useApplyAllFieldSuggestions(documentId: string) {
  const qc = useQueryClient()
  const detailKey = queryKeys.documents.detail(documentId)
  return useMutation({
    mutationFn: async (): Promise<DocumentDetail> => {
      const { data, error } = await apiClient.POST(
        '/dashboard/documents/{doc_uuid}/fields/apply-suggestions',
        {
          params: { path: { doc_uuid: documentId } },
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: (data) => {
      qc.setQueryData(detailKey, data)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: detailKey })
      qc.invalidateQueries({
        queryKey: queryKeys.documents.revisions(documentId),
      })
    },
  })
}


export function useTriggerFill(documentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: FillRequestBody): Promise<FillResponseBody> => {
      const { data, error } = await apiClient.POST(
        '/dashboard/documents/{doc_uuid}/fill',
        {
          params: { path: { doc_uuid: documentId } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents.detail(documentId) })
      qc.invalidateQueries({ queryKey: queryKeys.documents.jobs(documentId) })
      qc.invalidateQueries({
        queryKey: queryKeys.documents.revisions(documentId),
      })
    },
  })
}

/**
 * Ask the agent to suggest / rewrite / normalize field values.
 *
 * Proposals are written to ``DocumentField.suggested_value`` only — never
 * to ``current_value``. Acceptance happens through the existing
 * {@link usePatchSingleField} flow, which writes the FieldEdit audit row
 * with ``actor=agent`` so AI-assisted edits stay consistent with manual
 * edits in the history view.
 *
 * On success the document detail is invalidated so the Fields tab picks
 * up the new ``suggested_value`` markers without a manual refetch.
 *
 * Errors fall through to the caller. The backend translates upstream
 * rate-limit responses to HTTP 429 (with a ``Retry-After`` header) and
 * generic gateway errors to HTTP 502, so the UI can render a meaningful
 * message rather than a generic 500.
 */
export function useSuggestEdits(documentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: SuggestEditsBody): Promise<SuggestEditsResponseBody> => {
      const { data, error } = await apiClient.POST(
        '/dashboard/documents/{doc_uuid}/suggest-edits',
        {
          params: { path: { doc_uuid: documentId } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents.detail(documentId) })
    },
  })
}

export function useRenderDocument(documentId: string) {
  return useMutation({
    mutationFn: async (body: RenderRequestBody): Promise<RenderResponseBody> => {
      const { data, error } = await apiClient.POST(
        '/dashboard/documents/{doc_uuid}/render',
        {
          params: { path: { doc_uuid: documentId } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
  })
}

export function useDocumentAudit(documentId: string, actor?: EditActor) {
  return useQuery({
    queryKey: queryKeys.documents.audit(documentId, actor ?? 'all'),
    queryFn: async (): Promise<FieldEdit[]> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/documents/{doc_uuid}/audit',
        {
          params: {
            path: { doc_uuid: documentId },
            query: actor ? { actor } : {},
          },
        },
      )
      if (error) throw new ApiError(error)
      return data ?? []
    },
    enabled: Boolean(documentId),
  })
}

export function jobsRefetchInterval(jobs: JobRead[] | undefined): number | false {
  const latest = jobs?.[0]
  if (!latest) return false
  if (latest.status === 'pending' || latest.status === 'running') {
    return 1_500
  }
  if (
    latest.status === 'failed' &&
    (latest.is_retrying ||
      (latest.retry_after !== null && latest.retries < latest.max_retries))
  ) {
    return 5_000
  }
  return false
}

export function useDocumentJobs(uuid: string | undefined) {
  return useQuery({
    queryKey: queryKeys.documents.jobs(uuid ?? ''),
    queryFn: async (): Promise<JobRead[]> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/documents/{doc_uuid}/jobs',
        { params: { path: { doc_uuid: uuid! } } },
      )
      if (error) throw new ApiError(error)
      return data ?? []
    },
    enabled: Boolean(uuid),
    refetchInterval: (query) =>
      jobsRefetchInterval(query.state.data as JobRead[] | undefined),
  })
}

/**
 * Newest-first list of revision entries for one document.
 *
 * Each entry summarises one save / fill batch — author, change count,
 * source run id, parent revision uuid. The detail endpoint
 * ({@link useDocumentRevision}) hydrates the per-field diff on demand.
 */
export function useDocumentRevisions(documentId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.documents.revisions(documentId ?? ''),
    queryFn: async (): Promise<RevisionSummary[]> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/documents/{doc_uuid}/revisions',
        { params: { path: { doc_uuid: documentId! } } },
      )
      if (error) throw new ApiError(error)
      return data ?? []
    },
    enabled: Boolean(documentId),
  })
}

/**
 * Per-revision diff. ``revisionUuid === undefined`` keeps the query idle —
 * the history panel opens a dialog on row click and only then enables this.
 */
export function useDocumentRevision(
  documentId: string | undefined,
  revisionUuid: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.documents.revision(
      documentId ?? '',
      revisionUuid ?? '',
    ),
    queryFn: async (): Promise<RevisionDetail> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/documents/{doc_uuid}/revisions/{revision_uuid}',
        {
          params: {
            path: {
              doc_uuid: documentId!,
              revision_uuid: revisionUuid!,
            },
          },
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    enabled: Boolean(documentId) && Boolean(revisionUuid),
  })
}

/**
 * Re-enqueue parsing for a document whose previous attempt failed with a
 * retryable error code. The backend rejects (409) when the failure isn't
 * retryable, so the caller should hide the Retry button via
 * ``doc.is_retryable`` rather than relying on the error response.
 */
export function useRetryDocument(documentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (): Promise<DocumentSummary> => {
      const { data, error } = await apiClient.POST(
        '/dashboard/documents/{doc_uuid}/retry',
        { params: { path: { doc_uuid: documentId } } },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: (data) => {
      // Optimistically prime the detail-page query so the UI flips out of
      // the error state immediately, before the next poll round-trip.
      qc.setQueryData<DocumentDetail | undefined>(
        queryKeys.documents.detail(documentId),
        (prev) =>
          prev
            ? {
                ...prev,
                status: data.status,
                error_code: data.error_code,
                error_message: data.error_message,
                friendly_error: data.friendly_error,
                progress_stage: data.progress_stage,
                progress_percent: data.progress_percent,
                is_retryable: data.is_retryable,
              }
            : prev,
      )
      qc.invalidateQueries({ queryKey: queryKeys.documents.all })
      qc.invalidateQueries({ queryKey: queryKeys.documents.jobs(documentId) })
    },
  })
}

export function useDeleteDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await apiClient.DELETE(
        '/dashboard/documents/{doc_uuid}',
        { params: { path: { doc_uuid: id } } },
      )
      if (error) throw new ApiError(error)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents.all })
    },
  })
}

export function useUpdateDocument(documentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: DocumentUpdateBody): Promise<DocumentSummary> => {
      const { data, error } = await apiClient.PATCH(
        '/dashboard/documents/{doc_uuid}',
        {
          params: { path: { doc_uuid: documentId } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: (data) => {
      qc.setQueryData<DocumentDetail | undefined>(
        queryKeys.documents.detail(documentId),
        (prev) => (prev ? { ...prev, ...data } : prev),
      )
      qc.invalidateQueries({ queryKey: queryKeys.documents.all })
      qc.invalidateQueries({ queryKey: queryKeys.documents.detail(documentId) })
    },
  })
}

// ---------------------------------------------------------------------------
// /dashboard/workspace-context — reusable saved-context blobs that the
// autofill pipeline draws on to pre-populate fields. There are two scopes:
//
//   - user-scope    → singleton per user; the "workspace default"
//   - document-scope → one row per document; per-doc reusable context
//
// Each mutation is optimistic and rolls back the cache on failure so the
// dialogs can communicate "saved" instantly while still honouring the
// network as the source of truth.
// ---------------------------------------------------------------------------

// Cache shape used by ``onMutate`` to materialise the optimistic next-state
// for a workspace-context row. Keeps a stable timestamp the test asserts
// against so the optimistic write doesn't read from ``Date.now()``.
function makeOptimisticContext(
  previous: WorkspaceContextRead | null | undefined,
  content: string,
  scope: ContextScope,
  scopeRefId: number | null = null,
): WorkspaceContextRead {
  return {
    content,
    scope,
    scope_ref_id: previous?.scope_ref_id ?? scopeRefId ?? null,
    updated_at: new Date().toISOString(),
  }
}

export function useWorkspaceContext() {
  return useQuery({
    queryKey: queryKeys.workspaceContext,
    queryFn: async (): Promise<WorkspaceContextRead | null> => {
      const { data, error } = await apiClient.GET('/dashboard/workspace-context')
      if (error) throw new ApiError(error)
      return data ?? null
    },
  })
}

export function useSaveWorkspaceContext() {
  const qc = useQueryClient()
  return useMutation<
    WorkspaceContextRead,
    Error,
    WorkspaceContextWrite,
    WorkspaceContextRollback<typeof queryKeys.workspaceContext>
  >({
    mutationFn: async (body) => {
      const { data, error } = await apiClient.PUT(
        '/dashboard/workspace-context',
        { body },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: queryKeys.workspaceContext })
      const previous = qc.getQueryData<WorkspaceContextRead | null>(
        queryKeys.workspaceContext,
      )
      qc.setQueryData<WorkspaceContextRead | null>(
        queryKeys.workspaceContext,
        makeOptimisticContext(previous, body.content, 'user'),
      )
      return { queryKey: queryKeys.workspaceContext, previous }
    },
    onError: (_err, _body, context) => {
      if (context) qc.setQueryData(context.queryKey, context.previous ?? null)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: queryKeys.workspaceContext })
      qc.invalidateQueries({ queryKey: queryKeys.workspaceContextAudit })
    },
  })
}

export function useClearWorkspaceContext() {
  const qc = useQueryClient()
  return useMutation<
    void,
    Error,
    void,
    WorkspaceContextRollback<typeof queryKeys.workspaceContext>
  >({
    mutationFn: async () => {
      const { error } = await apiClient.DELETE('/dashboard/workspace-context')
      if (error) throw new ApiError(error)
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: queryKeys.workspaceContext })
      const previous = qc.getQueryData<WorkspaceContextRead | null>(
        queryKeys.workspaceContext,
      )
      qc.setQueryData<WorkspaceContextRead | null>(
        queryKeys.workspaceContext,
        null,
      )
      return { queryKey: queryKeys.workspaceContext, previous }
    },
    onError: (_err, _vars, context) => {
      if (context) qc.setQueryData(context.queryKey, context.previous ?? null)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: queryKeys.workspaceContext })
      qc.invalidateQueries({ queryKey: queryKeys.workspaceContextAudit })
    },
  })
}

export function useWorkspaceContextAudit(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.workspaceContextAudit,
    queryFn: async (): Promise<WorkspaceContextAuditEvent[]> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/workspace-context/audit',
      )
      if (error) throw new ApiError(error)
      return data ?? []
    },
    enabled: opts.enabled ?? true,
  })
}

// ─────────────────── document-scope reusable context ─────────────────────

export function useDocumentWorkspaceContext(documentId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.documentWorkspaceContext(documentId ?? ''),
    queryFn: async (): Promise<WorkspaceContextRead | null> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/documents/{doc_uuid}/workspace-context',
        { params: { path: { doc_uuid: documentId! } } },
      )
      if (error) throw new ApiError(error)
      return data ?? null
    },
    enabled: Boolean(documentId),
  })
}

export function useSaveDocumentWorkspaceContext(documentId: string) {
  const qc = useQueryClient()
  const key = queryKeys.documentWorkspaceContext(documentId)
  return useMutation<
    WorkspaceContextRead,
    Error,
    WorkspaceContextWrite,
    WorkspaceContextRollback<typeof key>
  >({
    mutationFn: async (body) => {
      const { data, error } = await apiClient.PUT(
        '/dashboard/documents/{doc_uuid}/workspace-context',
        {
          params: { path: { doc_uuid: documentId } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<WorkspaceContextRead | null>(key)
      qc.setQueryData<WorkspaceContextRead | null>(
        key,
        makeOptimisticContext(previous, body.content, 'document'),
      )
      return { queryKey: key, previous }
    },
    onError: (_err, _vars, context) => {
      if (context) qc.setQueryData(context.queryKey, context.previous ?? null)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key })
      qc.invalidateQueries({
        queryKey: queryKeys.documentWorkspaceContextAudit(documentId),
      })
    },
  })
}

export function useClearDocumentWorkspaceContext(documentId: string) {
  const qc = useQueryClient()
  const key = queryKeys.documentWorkspaceContext(documentId)
  return useMutation<
    void,
    Error,
    void,
    WorkspaceContextRollback<typeof key>
  >({
    mutationFn: async () => {
      const { error } = await apiClient.DELETE(
        '/dashboard/documents/{doc_uuid}/workspace-context',
        { params: { path: { doc_uuid: documentId } } },
      )
      if (error) throw new ApiError(error)
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<WorkspaceContextRead | null>(key)
      qc.setQueryData<WorkspaceContextRead | null>(key, null)
      return { queryKey: key, previous }
    },
    onError: (_err, _vars, context) => {
      if (context) qc.setQueryData(context.queryKey, context.previous ?? null)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key })
      qc.invalidateQueries({
        queryKey: queryKeys.documentWorkspaceContextAudit(documentId),
      })
    },
  })
}

export function useDocumentWorkspaceContextAudit(
  documentId: string | undefined,
  opts: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: queryKeys.documentWorkspaceContextAudit(documentId ?? ''),
    queryFn: async (): Promise<WorkspaceContextAuditEvent[]> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/documents/{doc_uuid}/workspace-context/audit',
        { params: { path: { doc_uuid: documentId! } } },
      )
      if (error) throw new ApiError(error)
      return data ?? []
    },
    enabled: Boolean(documentId) && (opts.enabled ?? true),
  })
}

interface DocumentContextRollback {
  previous: DocumentDetail | undefined
}

export function usePatchDocumentContext(documentId: string) {
  const qc = useQueryClient()
  const detailKey = queryKeys.documents.detail(documentId)
  return useMutation<
    DocumentDetail,
    Error,
    DocumentContextUpdate,
    DocumentContextRollback
  >({
    mutationFn: async (body) => {
      const { data, error } = await apiClient.PATCH(
        '/dashboard/documents/{doc_uuid}/context',
        {
          params: { path: { doc_uuid: documentId } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    // Optimistically reflect the new snapshot so the precedence panel and
    // "Applied" badge flip instantly. The detail-poll + onSettled invalidation
    // reconciles against the server.
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: detailKey })
      const previous = qc.getQueryData<DocumentDetail>(detailKey)
      if (previous) {
        qc.setQueryData<DocumentDetail>(detailKey, {
          ...previous,
          initial_context: body.initial_context ?? null,
        })
      }
      return { previous }
    },
    onError: (_err, _body, context) => {
      if (context?.previous) qc.setQueryData(detailKey, context.previous)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: detailKey })
    },
  })
}

// ---------------------------------------------------------------------------
// /dashboard/workspaces/{workspace_uuid}/saved-prompts — workspace-scoped
// reusable fill-prompt library
// ---------------------------------------------------------------------------
//
// Optimistic mutations: create / update / delete patch the cached list
// before the server responds so the UI feels immediate. On error we roll
// back to the prior snapshot. Conflict / validation errors bubble to the
// caller (toast in the page) — we do NOT swallow them.

export function useSavedPrompts(
  workspaceUuid: string | null | undefined,
  projectUuid?: string | null,
) {
  return useQuery({
    queryKey: queryKeys.savedPrompts.list(workspaceUuid ?? '', projectUuid),
    queryFn: async (): Promise<SavedPromptRead[]> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/workspaces/{workspace_uuid}/saved-prompts',
        {
          params: {
            path: { workspace_uuid: workspaceUuid! },
            query: projectUuid ? { project_uuid: projectUuid } : undefined,
          },
        },
      )
      if (error) throw new ApiError(error)
      return data ?? []
    },
    enabled: Boolean(workspaceUuid),
  })
}

interface SavedPromptListContext {
  previous: SavedPromptRead[] | undefined
}

export function useCreateSavedPrompt(workspaceUuid: string) {
  const qc = useQueryClient()
  const listKey = queryKeys.savedPrompts.list(workspaceUuid)
  return useMutation<SavedPromptRead, Error, SavedPromptCreate, SavedPromptListContext>({
    mutationFn: async (body): Promise<SavedPromptRead> => {
      const { data, error } = await apiClient.POST(
        '/dashboard/workspaces/{workspace_uuid}/saved-prompts',
        {
          params: { path: { workspace_uuid: workspaceUuid } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: listKey })
      const previous = qc.getQueryData<SavedPromptRead[]>(listKey)
      // Stub UUID so the list renders the new card immediately — the real
      // UUID lands when ``onSuccess`` writes the server row back over it.
      const now = new Date().toISOString()
      const stub: SavedPromptRead = {
        uuid: `optimistic-${Math.random().toString(36).slice(2)}`,
        workspace_uuid: workspaceUuid,
        project_uuid: body.project_uuid ?? null,
        name: body.name.trim(),
        content: body.content.trim(),
        created_by_email: null,
        created_at: now,
        updated_at: now,
      }
      qc.setQueryData<SavedPromptRead[]>(listKey, (current) =>
        current ? [stub, ...current] : [stub],
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        qc.setQueryData(listKey, context.previous)
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: queryKeys.savedPrompts.all })
    },
  })
}

interface UpdateSavedPromptVars {
  promptUuid: string
  body: SavedPromptUpdate
}

export function useUpdateSavedPrompt(workspaceUuid: string) {
  const qc = useQueryClient()
  const listKey = queryKeys.savedPrompts.list(workspaceUuid)
  return useMutation<
    SavedPromptRead,
    Error,
    UpdateSavedPromptVars,
    SavedPromptListContext
  >({
    mutationFn: async ({ promptUuid, body }): Promise<SavedPromptRead> => {
      const { data, error } = await apiClient.PATCH(
        '/dashboard/workspaces/{workspace_uuid}/saved-prompts/{prompt_uuid}',
        {
          params: {
            path: { workspace_uuid: workspaceUuid, prompt_uuid: promptUuid },
          },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onMutate: async ({ promptUuid, body }) => {
      await qc.cancelQueries({ queryKey: listKey })
      const previous = qc.getQueryData<SavedPromptRead[]>(listKey)
      qc.setQueryData<SavedPromptRead[]>(listKey, (current) =>
        current?.map((row) =>
          row.uuid === promptUuid
            ? {
                ...row,
                name: body.name?.trim() ?? row.name,
                content: body.content?.trim() ?? row.content,
                project_uuid:
                  'project_uuid' in body
                    ? (body.project_uuid ?? null)
                    : row.project_uuid,
                updated_at: new Date().toISOString(),
              }
            : row,
        ),
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        qc.setQueryData(listKey, context.previous)
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: queryKeys.savedPrompts.all })
    },
  })
}

export function useDeleteSavedPrompt(workspaceUuid: string) {
  const qc = useQueryClient()
  const listKey = queryKeys.savedPrompts.list(workspaceUuid)
  return useMutation<void, Error, string, SavedPromptListContext>({
    mutationFn: async (promptUuid): Promise<void> => {
      const { error } = await apiClient.DELETE(
        '/dashboard/workspaces/{workspace_uuid}/saved-prompts/{prompt_uuid}',
        {
          params: {
            path: { workspace_uuid: workspaceUuid, prompt_uuid: promptUuid },
          },
        },
      )
      if (error) throw new ApiError(error)
    },
    onMutate: async (promptUuid) => {
      await qc.cancelQueries({ queryKey: listKey })
      const previous = qc.getQueryData<SavedPromptRead[]>(listKey)
      qc.setQueryData<SavedPromptRead[]>(listKey, (current) =>
        current?.filter((row) => row.uuid !== promptUuid),
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        qc.setQueryData(listKey, context.previous)
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: queryKeys.savedPrompts.all })
    },
  })
}

// ---------------------------------------------------------------------------
// /dashboard/documents/{uuid}/effective-instructions — precedence preview
// ---------------------------------------------------------------------------
export interface EffectiveInstructionsParams {
  savedPromptUuid?: string | null
  userInstructions?: string | null
}

export function useEffectiveInstructions(
  documentId: string | undefined,
  params: EffectiveInstructionsParams = {},
  options: { enabled?: boolean } = {},
) {
  const { savedPromptUuid, userInstructions } = params
  return useQuery({
    queryKey: queryKeys.effectiveInstructions(documentId ?? '', {
      saved_prompt_uuid: savedPromptUuid ?? null,
      user_instructions: userInstructions ?? null,
    }),
    queryFn: async (): Promise<EffectiveInstructions> => {
      // POST (not GET): ``user_instructions`` is free-form chat text and
      // we don't want it leaking into URLs, browser history, or access
      // logs — and long drafts can also exceed URL-length limits. The
      // endpoint is still read-only / idempotent on the server.
      const body: {
        saved_prompt_uuid?: string | null
        apply_project_context: boolean
        user_instructions?: string | null
      } = { apply_project_context: true }
      if (savedPromptUuid) body.saved_prompt_uuid = savedPromptUuid
      if (userInstructions !== undefined && userInstructions !== null) {
        body.user_instructions = userInstructions
      }
      const { data, error } = await apiClient.POST(
        '/dashboard/documents/{doc_uuid}/effective-instructions',
        {
          params: {
            path: { doc_uuid: documentId! },
          },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    enabled: Boolean(documentId) && (options.enabled ?? true),
  })
}

// ---------------------------------------------------------------------------
// /dashboard/chats — persisted chat transcripts for past fill runs
// ---------------------------------------------------------------------------
export function useChats() {
  return useQuery({
    queryKey: queryKeys.chats.list,
    queryFn: async (): Promise<ChatSessionSummary[]> => {
      const { data, error } = await apiClient.GET('/dashboard/chats')
      if (error) throw new ApiError(error)
      return data ?? []
    },
  })
}

export function useDocumentChats(documentId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.documents.chats(documentId ?? ''),
    queryFn: async (): Promise<ChatSessionSummary[]> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/documents/{doc_uuid}/chats',
        { params: { path: { doc_uuid: documentId! } } },
      )
      if (error) throw new ApiError(error)
      return data ?? []
    },
    enabled: Boolean(documentId),
  })
}

export function useChat(sessionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.chats.detail(sessionId ?? ''),
    queryFn: async (): Promise<ChatSessionDetail> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/chats/{session_uuid}',
        { params: { path: { session_uuid: sessionId! } } },
      )
      if (error) throw new ApiError(error)
      return data
    },
    enabled: Boolean(sessionId),
  })
}
// ---------------------------------------------------------------------------
// /dashboard/documents/{doc_uuid}/overlays — document-overlay CRUD + status
// transitions. The store layer in ``stores/overlays`` builds on top of these
// hooks; consumers that only need plain server state can use them directly.
// ---------------------------------------------------------------------------

export interface OverlayListFilter {
  type?: OverlayType | null
  status?: OverlayStatus | null
  page?: number | null
}

export function useOverlays(
  documentId: string | undefined,
  filter?: OverlayListFilter,
) {
  return useQuery({
    queryKey: queryKeys.documents.overlays(documentId ?? '', filter),
    queryFn: async (): Promise<Overlay[]> => {
      const params: {
        path: { doc_uuid: string }
        query?: { type?: OverlayType; status?: OverlayStatus; page?: number }
      } = { path: { doc_uuid: documentId! } }
      const q: { type?: OverlayType; status?: OverlayStatus; page?: number } = {}
      if (filter?.type) q.type = filter.type
      if (filter?.status) q.status = filter.status
      if (typeof filter?.page === 'number') q.page = filter.page
      if (Object.keys(q).length > 0) {
        params.query = q
      }
      const { data, error } = await apiClient.GET(
        '/dashboard/documents/{doc_uuid}/overlays',
        { params },
      )
      if (error) throw new ApiError(error)
      return data ?? []
    },
    enabled: Boolean(documentId),
  })
}

export function useCreateOverlay(documentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: OverlayCreateBody): Promise<Overlay> => {
      const { data, error } = await apiClient.POST(
        '/dashboard/documents/{doc_uuid}/overlays',
        { params: { path: { doc_uuid: documentId } }, body },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: () => {
      // Invalidate every cached filter slice — list params live in the
      // query key, so a coarse predicate is the simplest way to refetch.
      qc.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === 'documents' &&
          q.queryKey[1] === 'overlays' &&
          q.queryKey[2] === documentId,
      })
    },
  })
}

export function useUpdateOverlay(documentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      overlayId,
      body,
    }: {
      overlayId: string
      body: OverlayUpdateBody
    }): Promise<Overlay> => {
      const { data, error } = await apiClient.PATCH(
        '/dashboard/documents/{doc_uuid}/overlays/{overlay_uuid}',
        {
          params: { path: { doc_uuid: documentId, overlay_uuid: overlayId } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === 'documents' &&
          q.queryKey[1] === 'overlays' &&
          q.queryKey[2] === documentId,
      })
    },
  })
}

export function useTransitionOverlay(documentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      overlayId,
      body,
    }: {
      overlayId: string
      body: OverlayTransitionBody
    }): Promise<Overlay> => {
      const { data, error } = await apiClient.POST(
        '/dashboard/documents/{doc_uuid}/overlays/{overlay_uuid}/transition',
        {
          params: { path: { doc_uuid: documentId, overlay_uuid: overlayId } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === 'documents' &&
          q.queryKey[1] === 'overlays' &&
          q.queryKey[2] === documentId,
      })
    },
  })
}

export function useDeleteOverlay(documentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (overlayId: string): Promise<void> => {
      const { error } = await apiClient.DELETE(
        '/dashboard/documents/{doc_uuid}/overlays/{overlay_uuid}',
        {
          params: { path: { doc_uuid: documentId, overlay_uuid: overlayId } },
        },
      )
      if (error) throw new ApiError(error)
    },
    onSuccess: () => {
      qc.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === 'documents' &&
          q.queryKey[1] === 'overlays' &&
          q.queryKey[2] === documentId,
      })
    },
  })
}
/**
 * Merge a realtime ``field:updated`` event into the document detail cache
 * without round-tripping through ``useDocument``. Called from the realtime
 * hook so the Fields panel sees other users' edits live.
 */
export function applyRealtimeFieldUpdate(
  qc: ReturnType<typeof useQueryClient>,
  documentId: string,
  update: {
    uuid: string
    current_value: string | null
    suggested_value: string | null
    accepted_by: string | null
    revision: number
    updated_at: string
  },
  documentRevision: number,
): void {
  const detailKey = queryKeys.documents.detail(documentId)
  const previous = qc.getQueryData<DocumentDetail>(detailKey)
  if (!previous) return
  let touched = false
  const fields = previous.fields.map((f) => {
    if (f.uuid !== update.uuid) return f
    if ((f.revision ?? 0) >= update.revision) return f
    touched = true
    const ab = update.accepted_by
    // accepted_by is raw string from wire — cast after narrowing
    const acceptedBy: 'user' | 'agent' | null =
      ab === 'user' || ab === 'agent' ? ab : null
    return {
      ...f,
      current_value: update.current_value,
      suggested_value: update.suggested_value,
      accepted_by: acceptedBy,
      revision: update.revision,
      updated_at: update.updated_at,
    }
  })
  if (!touched && (previous.revision ?? 1) >= documentRevision) return
  qc.setQueryData<DocumentDetail>(detailKey, {
    ...previous,
    fields,
    revision:
      documentRevision > (previous.revision ?? 1)
        ? documentRevision
        : previous.revision,
  })
}

// ---------------------------------------------------------------------------
// /dashboard/ai-actions — explicit AI-suggested changes that require user
// review before they hit the document. The list endpoint is the audit trail;
// the apply/reject endpoints are the deliberate accept/reject boundary the
// document-detail UI exposes.
// ---------------------------------------------------------------------------

/**
 * List AI actions for a document. ``status`` filters server-side; pass
 * ``'pending'`` for the review panel and ``null`` for the full audit history.
 */
export function useDocumentAiActions(
  documentId: string | undefined,
  status?: AiActionStatus | null,
) {
  return useQuery({
    queryKey: queryKeys.aiActions.forDocument(documentId ?? '', status ?? null),
    queryFn: async (): Promise<AiActionRead[]> => {
      const { data, error, response } = await apiClient.GET(
        '/dashboard/documents/{doc_uuid}/ai-actions',
        {
          params: {
            path: { doc_uuid: documentId! },
            query: status ? { status } : {},
          },
        },
      )
      if (error) throw new ApiError(error, response?.status)
      return data ?? []
    },
    enabled: Boolean(documentId),
  })
}

/**
 * Accept an AI action: server applies it through the standard document
 * services so audit rows, checkbox canonicalisation, and per-field validation
 * all stay consistent with manual edits.
 *
 * On success, invalidates the document detail (so field values refresh), the
 * audit history (so Versions & audit shows the new applied row without a
 * manual refresh — broadly, across every actor filter), and the AI-actions list.
 */
export function useApplyAiAction(documentId: string | undefined) {
  const qc = useQueryClient()
  return useMutation<AiActionRead, ApiError, string>({
    mutationFn: async (actionUuid) => {
      const { data, error, response } = await apiClient.POST(
        '/dashboard/ai-actions/{action_uuid}/apply',
        { params: { path: { action_uuid: actionUuid } } },
      )
      if (error) throw new ApiError(error, response?.status)
      return data
    },
    onSuccess: () => {
      if (documentId) {
        qc.invalidateQueries({
          queryKey: queryKeys.documents.detail(documentId),
        })
        // Prefix-match every audit variant (all/user/ai/...) for this doc so
        // Versions & audit refreshes regardless of the active actor filter.
        qc.invalidateQueries({
          queryKey: ['documents', 'audit', documentId],
        })
        qc.invalidateQueries({
          queryKey: queryKeys.documents.revisions(documentId),
        })
      }
      qc.invalidateQueries({ queryKey: queryKeys.aiActions.all })
    },
  })
}

// ---------------------------------------------------------------------------
// Collaboration types
// ---------------------------------------------------------------------------
export type WorkspaceRead = components['schemas']['WorkspaceRead']
export type WorkspaceCreate = components['schemas']['WorkspaceCreate']
export type WorkspaceUpdate = components['schemas']['WorkspaceUpdate']
export type WorkspaceMemberRead = components['schemas']['WorkspaceMemberRead']
export type WorkspaceMemberUpdate = components['schemas']['WorkspaceMemberUpdate']
export type WorkspaceInviteRead = components['schemas']['WorkspaceInviteRead']
export type WorkspaceInviteCreate = components['schemas']['WorkspaceInviteCreate']
export type WorkspaceInviteCreateResponse = components['schemas']['WorkspaceInviteCreateResponse']
export type WorkspaceInviteAcceptRequest = components['schemas']['WorkspaceInviteAcceptRequest']
export type WorkspaceRole = components['schemas']['WorkspaceRole']
export type ProjectRead = components['schemas']['ProjectRead']
export type ProjectCreate = components['schemas']['ProjectCreate']
export type ProjectUpdate = components['schemas']['ProjectUpdate']
export type FolderRead = components['schemas']['FolderRead']
export type FolderCreate = components['schemas']['FolderCreate']
export type FolderUpdate = components['schemas']['FolderUpdate']
export type DocumentLocationUpdate = components['schemas']['DocumentLocationUpdate']
export type DocumentShareRead = components['schemas']['DocumentShareRead']
export type DocumentShareCreate = components['schemas']['DocumentShareCreate']
export type DocumentPartyRead = components['schemas']['DocumentPartyRead']
export type DocumentPartyCreate = components['schemas']['DocumentPartyCreate']
export type DocumentPartyRole = components['schemas']['DocumentPartyRole']
export type DocumentShareLinkRead = components['schemas']['DocumentShareLinkRead']
export type DocumentShareLinkCreate = components['schemas']['DocumentShareLinkCreate']
export type DocumentAccessEventRead = components['schemas']['DocumentAccessEventRead']
export type AuditLogRead = components['schemas']['AuditLogRead']

// ---------------------------------------------------------------------------
// /dashboard/workspaces — workspace CRUD
// ---------------------------------------------------------------------------
export function useWorkspaces() {
  return useQuery({
    queryKey: queryKeys.workspaces.list(),
    queryFn: async (): Promise<WorkspaceRead[]> => {
      const { data, error } = await apiClient.GET('/dashboard/workspaces')
      if (error) throw new ApiError(error)
      return data ?? []
    },
  })
}

export function useWorkspace(workspaceUuid: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workspaces.detail(workspaceUuid ?? ''),
    queryFn: async (): Promise<WorkspaceRead> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/workspaces/{workspace_uuid}',
        { params: { path: { workspace_uuid: workspaceUuid! } } },
      )
      if (error) throw new ApiError(error)
      return data
    },
    enabled: Boolean(workspaceUuid),
  })
}

/**
 * Optimistically insert a placeholder workspace into the list so the
 * sidebar switcher reflects the new entry before the round-trip
 * completes. The placeholder uuid is replaced with the server-issued one
 * in onSuccess so the new workspace is immediately addressable by the
 * `WorkspaceProvider` (which rejects unknown uuids defensively).
 */
const OPTIMISTIC_WORKSPACE_UUID_PREFIX = 'optimistic-'

export function useCreateWorkspace() {
  const qc = useQueryClient()
  return useOptimisticListMutation<WorkspaceRead, WorkspaceCreate, WorkspaceRead>({
    mutationFn: async (body) => {
      const { data, error } = await apiClient.POST('/dashboard/workspaces', {
        body,
      })
      if (error) throw new ApiError(error)
      return data
    },
    queryKey: queryKeys.workspaces.list(),
    applyOptimistic: (current, body) => [
      ...current,
      {
        uuid: `${OPTIMISTIC_WORKSPACE_UUID_PREFIX}${Date.now()}`,
        name: body.name,
        slug: body.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        is_personal: false,
        role: 'owner' as WorkspaceRole,
        member_count: 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    ],
    // Swap the optimistic placeholder for the server-issued row as soon as
    // the response lands — without this the list cache only carries the
    // real uuid after the post-settle refetch resolves, long enough for a
    // caller that does `await mutateAsync(...); setCurrentWorkspace(ws.uuid)`
    // to hit the WorkspaceProvider's defensive "uuid must be in the list"
    // guard and silently no-op.
    onSuccess: (data) => {
      qc.setQueryData<WorkspaceRead[]>(
        queryKeys.workspaces.list(),
        (current) => {
          const withoutPlaceholder = (current ?? []).filter(
            (w) => !w.uuid.startsWith(OPTIMISTIC_WORKSPACE_UUID_PREFIX),
          )
          if (withoutPlaceholder.some((w) => w.uuid === data.uuid)) {
            return withoutPlaceholder
          }
          return [...withoutPlaceholder, data]
        },
      )
    },
  })
}

export function useUpdateWorkspace(workspaceUuid: string) {
  const qc = useQueryClient()
  return useOptimisticListMutation<WorkspaceRead, WorkspaceUpdate, WorkspaceRead>({
    mutationFn: async (body) => {
      const { data, error } = await apiClient.PATCH(
        '/dashboard/workspaces/{workspace_uuid}',
        {
          params: { path: { workspace_uuid: workspaceUuid } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    queryKey: queryKeys.workspaces.list(),
    applyOptimistic: (current, body) =>
      current.map((w) =>
        w.uuid === workspaceUuid
          ? { ...w, name: body.name ?? w.name, updated_at: new Date().toISOString() }
          : w,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.workspaces.detail(workspaceUuid) })
    },
  })
}

export function useDeleteWorkspace() {
  return useOptimisticListMutation<void, string, WorkspaceRead>({
    mutationFn: async (workspaceUuid) => {
      const { error } = await apiClient.DELETE(
        '/dashboard/workspaces/{workspace_uuid}',
        { params: { path: { workspace_uuid: workspaceUuid } } },
      )
      if (error) throw new ApiError(error)
    },
    queryKey: queryKeys.workspaces.list(),
    applyOptimistic: (current, uuid) => current.filter((w) => w.uuid !== uuid),
  })
}

// ---------------------------------------------------------------------------
// /dashboard/workspaces/{workspace_uuid}/members — member management
// ---------------------------------------------------------------------------
export function useWorkspaceMembers(workspaceUuid: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workspaces.members(workspaceUuid ?? ''),
    queryFn: async (): Promise<WorkspaceMemberRead[]> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/workspaces/{workspace_uuid}/members',
        { params: { path: { workspace_uuid: workspaceUuid! } } },
      )
      if (error) throw new ApiError(error)
      return data ?? []
    },
    enabled: Boolean(workspaceUuid),
  })
}

/**
 * Optimistically updates the cached member's role so the role pill flips
 * immediately. The mutation's onError rolls back to ``previous`` if the
 * server rejects (e.g. trying to demote the last owner returns 409).
 */
export function useUpdateWorkspaceMember(workspaceUuid: string) {
  return useOptimisticListMutation<
    WorkspaceMemberRead,
    { memberUserId: string; body: WorkspaceMemberUpdate },
    WorkspaceMemberRead
  >({
    mutationFn: async ({ memberUserId, body }) => {
      const { data, error } = await apiClient.PATCH(
        '/dashboard/workspaces/{workspace_uuid}/members/{member_user_id}',
        {
          params: { path: { workspace_uuid: workspaceUuid, member_user_id: memberUserId } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    queryKey: queryKeys.workspaces.members(workspaceUuid),
    applyOptimistic: (current, { memberUserId, body }) =>
      current.map((m) =>
        m.user_id === memberUserId ? { ...m, role: body.role } : m,
      ),
    // A member-role change can shift the *viewer's* effective permissions
    // if it's their own row — invalidate the whole workspaces prefix so the
    // sidebar role badge, member list, and any other cached slice update.
    invalidateKeys: [queryKeys.workspaces.all],
  })
}

export function useRemoveWorkspaceMember(workspaceUuid: string) {
  return useOptimisticListMutation<void, string, WorkspaceMemberRead>({
    mutationFn: async (memberUserId) => {
      const { error } = await apiClient.DELETE(
        '/dashboard/workspaces/{workspace_uuid}/members/{member_user_id}',
        {
          params: { path: { workspace_uuid: workspaceUuid, member_user_id: memberUserId } },
        },
      )
      if (error) throw new ApiError(error)
    },
    queryKey: queryKeys.workspaces.members(workspaceUuid),
    applyOptimistic: (current, memberUserId) =>
      current.filter((m) => m.user_id !== memberUserId),
    invalidateKeys: [queryKeys.workspaces.list()],
  })
}

// ---------------------------------------------------------------------------
// /dashboard/workspaces/{workspace_uuid}/invites — invite management
// ---------------------------------------------------------------------------
export function useWorkspaceInvites(workspaceUuid: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workspaces.invites(workspaceUuid ?? ''),
    queryFn: async (): Promise<WorkspaceInviteRead[]> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/workspaces/{workspace_uuid}/invites',
        { params: { path: { workspace_uuid: workspaceUuid! } } },
      )
      if (error) throw new ApiError(error)
      return data ?? []
    },
    enabled: Boolean(workspaceUuid),
  })
}

export function useCreateWorkspaceInvite(workspaceUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      body: WorkspaceInviteCreate,
    ): Promise<WorkspaceInviteCreateResponse> => {
      const { data, error } = await apiClient.POST(
        '/dashboard/workspaces/{workspace_uuid}/invites',
        {
          params: { path: { workspace_uuid: workspaceUuid } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.workspaces.invites(workspaceUuid) })
    },
  })
}

export function useRevokeWorkspaceInvite(workspaceUuid: string) {
  return useOptimisticListMutation<void, string, WorkspaceInviteRead>({
    mutationFn: async (inviteUuid) => {
      const { error } = await apiClient.DELETE(
        '/dashboard/workspaces/{workspace_uuid}/invites/{invite_uuid}',
        {
          params: { path: { workspace_uuid: workspaceUuid, invite_uuid: inviteUuid } },
        },
      )
      if (error) throw new ApiError(error)
    },
    queryKey: queryKeys.workspaces.invites(workspaceUuid),
    // Mark as revoked *in place* rather than removing — the list filter at
    // the call site hides revoked entries, but keeping the row prevents a
    // visual jump if the user pulls up the audit log next.
    applyOptimistic: (current, inviteUuid) =>
      current.map((i) =>
        i.uuid === inviteUuid
          ? { ...i, revoked_at: new Date().toISOString() }
          : i,
      ),
  })
}

/**
 * Re-issue an invite for the same email + role by POSTing a fresh
 * invite. The backend's create-invite handler atomically revokes any
 * outstanding invites for the same (workspace, email) inside the same
 * DB transaction before inserting the new row (see
 * ``backend/app/services/invites.py``), so a single POST is the
 * rotation — there's no window where the user has no valid invite. We
 * deliberately do NOT issue a separate DELETE first: a DELETE that
 * succeeds followed by a POST that fails would leave the invitee with
 * no usable token until an admin notices and re-invites.
 *
 * The ``uuid`` of the existing invite is accepted only so the UI can
 * disambiguate per-row pending state; it is not sent to the server.
 */
export function useResendWorkspaceInvite(workspaceUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      invite: Pick<WorkspaceInviteRead, 'uuid' | 'email' | 'role'>,
    ): Promise<WorkspaceInviteCreateResponse> => {
      const { data, error } = await apiClient.POST(
        '/dashboard/workspaces/{workspace_uuid}/invites',
        {
          params: { path: { workspace_uuid: workspaceUuid } },
          body: { email: invite.email, role: invite.role },
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.workspaces.invites(workspaceUuid) })
    },
  })
}

// ---------------------------------------------------------------------------
// /dashboard/invites/accept — accept a workspace invite by token
// ---------------------------------------------------------------------------
export function useAcceptWorkspaceInvite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: WorkspaceInviteAcceptRequest): Promise<WorkspaceRead> => {
      const { data, error } = await apiClient.POST('/dashboard/invites/accept', {
        body,
      })
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.workspaces.all })
    },
  })
}

// ---------------------------------------------------------------------------
// /dashboard/workspaces/{workspace_uuid}/audit — workspace audit log
// ---------------------------------------------------------------------------
export function useWorkspaceAudit(
  workspaceUuid: string | undefined,
  params: WorkspaceAuditParams = {},
) {
  const { limit, offset } = params
  return useQuery({
    queryKey: queryKeys.workspaces.audit(workspaceUuid ?? '', { limit, offset }),
    queryFn: async (): Promise<AuditLogRead[]> => {
      const query: { limit?: number; offset?: number } = {}
      if (limit !== undefined) query.limit = limit
      if (offset !== undefined) query.offset = offset
      const { data, error } = await apiClient.GET(
        '/dashboard/workspaces/{workspace_uuid}/audit',
        {
          params: {
            path: { workspace_uuid: workspaceUuid! },
            query,
          },
        },
      )
      if (error) throw new ApiError(error)
      return data ?? []
    },
    enabled: Boolean(workspaceUuid),
  })
}

// ---------------------------------------------------------------------------
// /dashboard/workspaces/{workspace_uuid}/projects — project CRUD
// ---------------------------------------------------------------------------
/**
 * Workspace projects list. ``q`` is a server-side case-insensitive substring
 * filter against ``name`` + ``description`` — empty or whitespace-only values
 * are treated as "no filter" and never sent so the cache key matches the
 * unfiltered slice that mutations target optimistically.
 */
export function useProjects(
  workspaceUuid: string | undefined,
  q?: string | null,
) {
  const trimmed = q?.trim() || null
  return useQuery({
    queryKey: queryKeys.workspaces.projects.list(workspaceUuid ?? '', trimmed),
    queryFn: async (): Promise<ProjectRead[]> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/workspaces/{workspace_uuid}/projects',
        {
          params: {
            path: { workspace_uuid: workspaceUuid! },
            query: trimmed ? { q: trimmed } : undefined,
          },
        },
      )
      if (error) throw new ApiError(error)
      return data ?? []
    },
    enabled: Boolean(workspaceUuid),
  })
}

export function useCreateProject(workspaceUuid: string) {
  return useOptimisticListMutation<ProjectRead, ProjectCreate, ProjectRead>({
    mutationFn: async (body) => {
      const { data, error } = await apiClient.POST(
        '/dashboard/workspaces/{workspace_uuid}/projects',
        {
          params: { path: { workspace_uuid: workspaceUuid } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    queryKey: queryKeys.workspaces.projects.list(workspaceUuid),
    applyOptimistic: (current, body) => [
      ...current,
      {
        uuid: `optimistic-${Date.now()}`,
        workspace_uuid: workspaceUuid,
        name: body.name,
        slug: body.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        description: body.description ?? null,
        document_count: 0,
        folder_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    ],
    // Refetch every cached filter slice (unfiltered + any active ``q=``
    // search view) so the new project appears wherever the user is looking.
    invalidateKeys: [queryKeys.workspaces.projects.listAll(workspaceUuid)],
  })
}

export function useUpdateProject(workspaceUuid: string) {
  const qc = useQueryClient()
  return useOptimisticListMutation<
    ProjectRead,
    { projectUuid: string; body: ProjectUpdate },
    ProjectRead
  >({
    mutationFn: async ({ projectUuid, body }) => {
      const { data, error } = await apiClient.PATCH(
        '/dashboard/workspaces/{workspace_uuid}/projects/{project_uuid}',
        {
          params: { path: { workspace_uuid: workspaceUuid, project_uuid: projectUuid } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    queryKey: queryKeys.workspaces.projects.list(workspaceUuid),
    applyOptimistic: (current, { projectUuid, body }) =>
      current.map((p) =>
        p.uuid === projectUuid
          ? {
              ...p,
              name: body.name ?? p.name,
              description: body.description ?? p.description,
              updated_at: new Date().toISOString(),
            }
          : p,
      ),
    invalidateKeys: [queryKeys.workspaces.projects.listAll(workspaceUuid)],
    onSuccess: (_data, { projectUuid }) => {
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.projects.detail(workspaceUuid, projectUuid),
      })
    },
  })
}

export function useDeleteProject(workspaceUuid: string) {
  return useOptimisticListMutation<void, string, ProjectRead>({
    mutationFn: async (projectUuid) => {
      const { error } = await apiClient.DELETE(
        '/dashboard/workspaces/{workspace_uuid}/projects/{project_uuid}',
        {
          params: { path: { workspace_uuid: workspaceUuid, project_uuid: projectUuid } },
        },
      )
      if (error) throw new ApiError(error)
    },
    queryKey: queryKeys.workspaces.projects.list(workspaceUuid),
    applyOptimistic: (current, projectUuid) =>
      current.filter((p) => p.uuid !== projectUuid),
    invalidateKeys: [queryKeys.workspaces.projects.listAll(workspaceUuid)],
  })
}

// ---------------------------------------------------------------------------
// /dashboard/workspaces/{workspace_uuid}/projects/{project_uuid}/folders
// ---------------------------------------------------------------------------
export function useFolders(
  workspaceUuid: string | undefined,
  projectUuid: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.workspaces.projects.folders(
      workspaceUuid ?? '',
      projectUuid ?? '',
    ),
    queryFn: async (): Promise<FolderRead[]> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/workspaces/{workspace_uuid}/projects/{project_uuid}/folders',
        {
          params: {
            path: { workspace_uuid: workspaceUuid!, project_uuid: projectUuid! },
          },
        },
      )
      if (error) throw new ApiError(error)
      return data ?? []
    },
    enabled: Boolean(workspaceUuid) && Boolean(projectUuid),
  })
}

export function useCreateFolder(workspaceUuid: string, projectUuid: string) {
  return useOptimisticListMutation<FolderRead, FolderCreate, FolderRead>({
    mutationFn: async (body) => {
      const { data, error } = await apiClient.POST(
        '/dashboard/workspaces/{workspace_uuid}/projects/{project_uuid}/folders',
        {
          params: { path: { workspace_uuid: workspaceUuid, project_uuid: projectUuid } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    queryKey: queryKeys.workspaces.projects.folders(workspaceUuid, projectUuid),
    applyOptimistic: (current, body) => [
      ...current,
      {
        uuid: `optimistic-${Date.now()}`,
        project_uuid: projectUuid,
        parent_folder_uuid: body.parent_folder_uuid ?? null,
        name: body.name,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    ],
    // Project's folder_count is summarised on the project list — keep that
    // cache fresh so the badge in the projects index stays accurate after
    // a folder is added.
    invalidateKeys: [queryKeys.workspaces.projects.listAll(workspaceUuid)],
  })
}

export function useUpdateFolder(workspaceUuid: string, projectUuid: string) {
  return useOptimisticListMutation<
    FolderRead,
    { folderUuid: string; body: FolderUpdate },
    FolderRead
  >({
    mutationFn: async ({ folderUuid, body }) => {
      const { data, error } = await apiClient.PATCH(
        '/dashboard/workspaces/{workspace_uuid}/projects/{project_uuid}/folders/{folder_uuid}',
        {
          params: {
            path: {
              workspace_uuid: workspaceUuid,
              project_uuid: projectUuid,
              folder_uuid: folderUuid,
            },
          },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    queryKey: queryKeys.workspaces.projects.folders(workspaceUuid, projectUuid),
    applyOptimistic: (current, { folderUuid, body }) =>
      current.map((f) =>
        f.uuid === folderUuid
          ? {
              ...f,
              name: body.name ?? f.name,
              parent_folder_uuid:
                'parent_folder_uuid' in body
                  ? (body.parent_folder_uuid ?? null)
                  : f.parent_folder_uuid,
              updated_at: new Date().toISOString(),
            }
          : f,
      ),
  })
}

export function useDeleteFolder(workspaceUuid: string, projectUuid: string) {
  return useOptimisticListMutation<void, string, FolderRead>({
    mutationFn: async (folderUuid) => {
      const { error } = await apiClient.DELETE(
        '/dashboard/workspaces/{workspace_uuid}/projects/{project_uuid}/folders/{folder_uuid}',
        {
          params: {
            path: {
              workspace_uuid: workspaceUuid,
              project_uuid: projectUuid,
              folder_uuid: folderUuid,
            },
          },
        },
      )
      if (error) throw new ApiError(error)
    },
    queryKey: queryKeys.workspaces.projects.folders(workspaceUuid, projectUuid),
    // Drop the deleted folder AND any descendants so the tree doesn't
    // briefly render orphans pointing at a missing parent before the
    // refetch lands.
    applyOptimistic: (current, folderUuid) => {
      const dropped = new Set<string>([folderUuid])
      let grew = true
      while (grew) {
        grew = false
        for (const f of current) {
          if (
            f.parent_folder_uuid &&
            dropped.has(f.parent_folder_uuid) &&
            !dropped.has(f.uuid)
          ) {
            dropped.add(f.uuid)
            grew = true
          }
        }
      }
      return current.filter((f) => !dropped.has(f.uuid))
    },
    // Document list scoping changes — docs in the deleted folder fall
    // back to the project root, so any project-scoped doc list view needs
    // to refetch.
    invalidateKeys: [
      queryKeys.documents.all,
      queryKeys.workspaces.projects.listAll(workspaceUuid),
    ],
  })
}

// ---------------------------------------------------------------------------
// /dashboard/documents/{doc_uuid}/location — move a document within a
// workspace. Cross-workspace moves are NOT supported by the backend.
// ---------------------------------------------------------------------------
export function useUpdateDocumentLocation(
  documentId: string,
  workspaceUuid: string,
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: DocumentLocationUpdate) => {
      const { data, error } = await apiClient.PATCH(
        '/dashboard/documents/{doc_uuid}/location',
        {
          params: { path: { doc_uuid: documentId } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: () => {
      // The move changes which (workspace, project, folder) buckets the
      // doc appears in — invalidate every filtered list shape so the UI
      // pulls the canonical filter set.
      qc.invalidateQueries({ queryKey: queryKeys.documents.all })
      // The doc's detail is also affected (project/folder fields).
      qc.invalidateQueries({ queryKey: queryKeys.documents.detail(documentId) })
      // Moving across projects shifts the per-project ``document_count``
      // badge on /projects, so the workspace's project list must refetch.
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.projects.listAll(workspaceUuid),
      })
    },
  })
}

export function useMoveDocumentInWorkspace(workspaceUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      documentId,
      body,
    }: {
      documentId: string
      body: DocumentLocationUpdate
    }) => {
      const { data, error } = await apiClient.PATCH(
        '/dashboard/documents/{doc_uuid}/location',
        {
          params: { path: { doc_uuid: documentId } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.documents.all })
      qc.invalidateQueries({
        queryKey: queryKeys.documents.detail(variables.documentId),
      })
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.projects.listAll(workspaceUuid),
      })
    },
  })
}

/**
 * Reject an AI action: the audit row survives so the proposal is visible in
 * the history view alongside applied actions, but no fields are mutated.
 */
export function useRejectAiAction(documentId: string | undefined) {
  const qc = useQueryClient()
  return useMutation<AiActionRead, ApiError, string>({
    mutationFn: async (actionUuid) => {
      const { data, error, response } = await apiClient.POST(
        '/dashboard/ai-actions/{action_uuid}/reject',
        { params: { path: { action_uuid: actionUuid } } },
      )
      if (error) throw new ApiError(error, response?.status)
      return data
    },
    onSuccess: () => {
      if (documentId) {
        qc.invalidateQueries({
          queryKey: queryKeys.documents.detail(documentId),
        })
        // Prefix-match every audit variant (all/user/ai/...) for this doc so
        // Versions & audit refreshes regardless of the active actor filter.
        qc.invalidateQueries({
          queryKey: ['documents', 'audit', documentId],
        })
      }
      qc.invalidateQueries({ queryKey: queryKeys.aiActions.all })
    },
  })
}

// ---------------------------------------------------------------------------
// /dashboard/documents/{doc_uuid}/shares — document sharing
// ---------------------------------------------------------------------------
export function useDocumentShares(docUuid: string | undefined) {
  return useQuery({
    queryKey: queryKeys.documents.shares(docUuid ?? ''),
    queryFn: async (): Promise<DocumentShareRead[]> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/documents/{doc_uuid}/shares',
        { params: { path: { doc_uuid: docUuid! } } },
      )
      if (error) throw new ApiError(error)
      return data ?? []
    },
    enabled: Boolean(docUuid),
  })
}

export function useCreateDocumentShare(docUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: DocumentShareCreate): Promise<DocumentShareRead> => {
      const { data, error } = await apiClient.POST(
        '/dashboard/documents/{doc_uuid}/shares',
        {
          params: { path: { doc_uuid: docUuid } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents.shares(docUuid) })
    },
  })
}

export function useRevokeDocumentShare(docUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (shareUuid: string): Promise<void> => {
      const { error } = await apiClient.DELETE(
        '/dashboard/documents/{doc_uuid}/shares/{share_uuid}',
        {
          params: { path: { doc_uuid: docUuid, share_uuid: shareUuid } },
        },
      )
      if (error) throw new ApiError(error)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents.shares(docUuid) })
    },
  })
}

// ---------------------------------------------------------------------------
// /dashboard/documents/{doc_uuid}/parties + share-links — recipients
// ---------------------------------------------------------------------------
export function useDocumentParties(docUuid: string | undefined) {
  return useQuery({
    queryKey: queryKeys.documents.parties(docUuid ?? ''),
    queryFn: async (): Promise<DocumentPartyRead[]> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/documents/{doc_uuid}/parties',
        { params: { path: { doc_uuid: docUuid! } } },
      )
      if (error) throw new ApiError(error)
      return data ?? []
    },
    enabled: Boolean(docUuid),
  })
}

export function useCreateDocumentParty(docUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: DocumentPartyCreate): Promise<DocumentPartyRead> => {
      const { data, error } = await apiClient.POST(
        '/dashboard/documents/{doc_uuid}/parties',
        {
          params: { path: { doc_uuid: docUuid } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents.parties(docUuid) })
    },
  })
}

export function useDocumentShareLinks(docUuid: string | undefined) {
  return useQuery({
    queryKey: queryKeys.documents.shareLinks(docUuid ?? ''),
    queryFn: async (): Promise<DocumentShareLinkRead[]> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/documents/{doc_uuid}/share-links',
        { params: { path: { doc_uuid: docUuid! } } },
      )
      if (error) throw new ApiError(error)
      return data ?? []
    },
    enabled: Boolean(docUuid),
  })
}

export function useCreateDocumentShareLink(docUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      body: DocumentShareLinkCreate,
    ): Promise<DocumentShareLinkRead> => {
      const { data, error } = await apiClient.POST(
        '/dashboard/documents/{doc_uuid}/share-links',
        {
          params: { path: { doc_uuid: docUuid } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: (created) => {
      qc.setQueryData<DocumentShareLinkRead[]>(
        queryKeys.documents.shareLinks(docUuid),
        (existing = []) => [
          ...existing.filter((link) => link.uuid !== created.uuid),
          created,
        ],
      )
    },
  })
}

export function useRevokeDocumentShareLink(docUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (linkUuid: string): Promise<void> => {
      const { error } = await apiClient.DELETE(
        '/dashboard/documents/{doc_uuid}/share-links/{link_uuid}',
        {
          params: { path: { doc_uuid: docUuid, link_uuid: linkUuid } },
        },
      )
      if (error) throw new ApiError(error)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents.shareLinks(docUuid) })
      qc.invalidateQueries({ queryKey: queryKeys.documents.tracking(docUuid) })
    },
  })
}

export function useDocumentTracking(docUuid: string | undefined) {
  return useQuery({
    queryKey: queryKeys.documents.tracking(docUuid ?? ''),
    queryFn: async (): Promise<DocumentAccessEventRead[]> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/documents/{doc_uuid}/tracking',
        { params: { path: { doc_uuid: docUuid! } } },
      )
      if (error) throw new ApiError(error)
      return data ?? []
    },
    enabled: Boolean(docUuid),
  })
}

// ---------------------------------------------------------------------------
// /dashboard/documents/{doc_uuid}/chat — interactive AI chat with proposals
// ---------------------------------------------------------------------------

export interface SendChatMessageInput {
  message: string
  chat_session_uuid?: string | null
  context_files?: { r2_key: string }[] | null
  selection?: { field_uuids: string[] } | null
}

export function useSendChatMessage(documentId: string) {
  return useMutation({
    mutationFn: async (body: SendChatMessageInput): Promise<ChatMessageResponse> => {
      const { data, error } = await apiClient.POST(
        '/dashboard/documents/{doc_uuid}/chat',
        {
          params: { path: { doc_uuid: documentId } },
          body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
  })
}

// ---------------------------------------------------------------------------
// /dashboard/documents/{doc_uuid}/comments — anchored review comments
// ---------------------------------------------------------------------------
export function useDocumentComments(docUuid: string | undefined) {
  return useQuery({
    queryKey: queryKeys.documents.comments(docUuid ?? ''),
    queryFn: async (): Promise<DocumentComment[]> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/documents/{doc_uuid}/comments',
        { params: { path: { doc_uuid: docUuid! } } },
      )
      if (error) throw new ApiError(error)
      return data ?? []
    },
    enabled: Boolean(docUuid),
  })
}

export function useCreateComment(docUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: DocumentCommentCreate): Promise<DocumentComment> => {
      const { data, error } = await apiClient.POST(
        '/dashboard/documents/{doc_uuid}/comments',
        { params: { path: { doc_uuid: docUuid } }, body },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents.comments(docUuid) })
    },
  })
}

export function useUpdateComment(docUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: {
      commentUuid: string
      body: DocumentCommentUpdate
    }): Promise<DocumentComment> => {
      const { data, error } = await apiClient.PATCH(
        '/dashboard/documents/{doc_uuid}/comments/{comment_uuid}',
        {
          params: {
            path: { doc_uuid: docUuid, comment_uuid: vars.commentUuid },
          },
          body: vars.body,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents.comments(docUuid) })
    },
  })
}

export function useDeleteComment(docUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (commentUuid: string): Promise<void> => {
      const { error } = await apiClient.DELETE(
        '/dashboard/documents/{doc_uuid}/comments/{comment_uuid}',
        {
          params: { path: { doc_uuid: docUuid, comment_uuid: commentUuid } },
        },
      )
      if (error) throw new ApiError(error)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents.comments(docUuid) })
    },
  })
}

// ---------------------------------------------------------------------------
// /dashboard/documents/{doc_uuid}/proposals — proposal lifecycle
// ---------------------------------------------------------------------------

export function useDocumentProposals(
  documentId: string,
  proposalStatus?: EditProposalStatus | null,
) {
  return useQuery({
    queryKey: queryKeys.documents.proposals(documentId, proposalStatus),
    queryFn: async (): Promise<EditProposalRead[]> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/documents/{doc_uuid}/proposals',
        {
          params: {
            path: { doc_uuid: documentId },
            query: proposalStatus ? { status: proposalStatus } : {},
          },
        },
      )
      if (error) throw new ApiError(error)
      return data ?? []
    },
    enabled: Boolean(documentId),
  })
}

export function useAcceptProposal(documentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (proposalUuid: string): Promise<EditProposalRead> => {
      const { data, error } = await apiClient.POST(
        '/dashboard/documents/{doc_uuid}/proposals/{proposal_uuid}/accept',
        {
          params: { path: { doc_uuid: documentId, proposal_uuid: proposalUuid } },
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: (updated) => {
      // Optimistically reflect the accepted value in the cached document so
      // the Fields tab refreshes without waiting for the next poll cycle.
      if (updated.field_uuid && updated.proposed_value != null) {
        const fieldUuid = updated.field_uuid
        const proposedValue = updated.proposed_value
        qc.setQueryData<DocumentDetail | undefined>(
          queryKeys.documents.detail(documentId),
          (prev) =>
            prev
              ? {
                  ...prev,
                  fields: prev.fields.map((f) =>
                    f.uuid === fieldUuid
                      ? applyFieldPatch(f, {
                          current_value: proposedValue,
                          accepted_by: 'agent',
                          clear_suggested: true,
                        })
                      : f,
                  ),
                }
              : prev,
        )
      } else {
        qc.invalidateQueries({ queryKey: queryKeys.documents.detail(documentId) })
      }
    },
  })
}

export interface RejectProposalInput {
  proposalUuid: string
  reason?: string | null
}

export function useRejectProposal(documentId: string) {
  return useMutation({
    mutationFn: async ({ proposalUuid, reason }: RejectProposalInput): Promise<EditProposalRead> => {
      const { data, error } = await apiClient.POST(
        '/dashboard/documents/{doc_uuid}/proposals/{proposal_uuid}/reject',
        {
          params: { path: { doc_uuid: documentId, proposal_uuid: proposalUuid } },
          body: reason ? { reason } : null,
        },
      )
      if (error) throw new ApiError(error)
      return data
    },
  })
}

// ---------------------------------------------------------------------------
// /dashboard/documents/{doc_uuid}/suggestions — proposed field-value changes
// ---------------------------------------------------------------------------
export function useDocumentSuggestions(
  docUuid: string | undefined,
  statusFilter?: SuggestionStatus,
) {
  return useQuery({
    queryKey: [
      ...queryKeys.documents.suggestions(docUuid ?? ''),
      statusFilter ?? 'all',
    ],
    queryFn: async (): Promise<DocumentSuggestion[]> => {
      const { data, error } = await apiClient.GET(
        '/dashboard/documents/{doc_uuid}/suggestions',
        {
          params: {
            path: { doc_uuid: docUuid! },
            query: statusFilter ? { status: statusFilter } : {},
          },
        },
      )
      if (error) throw new ApiError(error)
      return data ?? []
    },
    enabled: Boolean(docUuid),
  })
}

export function useCreateSuggestion(docUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      body: DocumentSuggestionCreate,
    ): Promise<DocumentSuggestion> => {
      const { data, error } = await apiClient.POST(
        '/dashboard/documents/{doc_uuid}/suggestions',
        { params: { path: { doc_uuid: docUuid } }, body },
      )
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.documents.suggestions(docUuid),
      })
    },
  })
}

type SuggestionAction = 'accept' | 'reject' | 'withdraw'

export function useResolveSuggestion(docUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: {
      suggestionUuid: string
      action: SuggestionAction
    }): Promise<DocumentSuggestion> => {
      // The three endpoints are shape-identical (POST /<uuid>/<action>) so
      // they collapse into one mutation with a discriminator. Keeping
      // them under a single hook also lets callers cancel + invalidate the
      // suggestions list once regardless of which action ran.
      const path =
        vars.action === 'accept'
          ? '/dashboard/documents/{doc_uuid}/suggestions/{suggestion_uuid}/accept'
          : vars.action === 'reject'
            ? '/dashboard/documents/{doc_uuid}/suggestions/{suggestion_uuid}/reject'
            : '/dashboard/documents/{doc_uuid}/suggestions/{suggestion_uuid}/withdraw'
      const { data, error } = await apiClient.POST(path, {
        params: {
          path: { doc_uuid: docUuid, suggestion_uuid: vars.suggestionUuid },
        },
      })
      if (error) throw new ApiError(error)
      return data
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: queryKeys.documents.suggestions(docUuid),
      })
      // Accept writes the field value, so the document detail needs to be
      // re-fetched too (the SPA renders current_value off the detail cache).
      if (vars.action === 'accept') {
        qc.invalidateQueries({ queryKey: queryKeys.documents.detail(docUuid) })
        qc.invalidateQueries({
          queryKey: queryKeys.documents.audit(docUuid, 'all'),
        })
      }
    },
  })
}

// Re-export new types so consumers can import from this module.
export type { ChatContextSummary, ChatMessageResponse, EditProposalRead, EditProposalStatus }
